import {
    GetTypeProps,
    IsArrayType,
    IsIntersectionType,
    IsObjectType,
    ReplaceValuesByType,
    Type,
} from "../../reactor/Types/Type"
import { SubstituteAndDiscriminate } from "../../reactor/ReflectionInfo"
import { YTools } from "./YTools"
import * as Y from "yjs"

/**
 * Visits all the values in an object graph with their resolved types, allowing
 * you to return a new value by inspecting the value and its type (YTools
 * version of ReplaceValuesByType)
 */
export function ReplaceValuesByTypeYTools(
    value: any,
    type: Type,
    /** Returns the new value to replace the existing one with.
     *  Return the incoming `value` to leave it unchanged.
     */
    replacer: (v: any, t: Type, pathToProp: (string | number)[]) => any,
    visitedObjects = new Set<object>(),
    path: (string | number)[] = []
): any {
    // Fallback to the original implementation if value isn't from Y.js.
    if (!(value instanceof Y.Array || value instanceof Y.Map)) {
        return ReplaceValuesByType(value, type, replacer, visitedObjects, path)
    }

    const dt = SubstituteAndDiscriminate(YTools.valueToJSON(value), type)

    if (dt !== type) {
        return ReplaceValuesByTypeYTools(value, dt, replacer, visitedObjects, path)
    }

    value = replacer(value, type, path)

    if (IsObjectType(type) || IsIntersectionType(type)) {
        if (visitedObjects.has(value)) return value
        visitedObjects.add(value)

        const props = GetTypeProps(type)

        props.forEach((x) => {
            const oldValue = YTools.get(value, x.name)
            const newValue = ReplaceValuesByTypeYTools(oldValue, x.type, replacer, visitedObjects, [
                ...path,
                x.name,
            ])
            if (newValue !== oldValue) {
                YTools.set(value, x.name, newValue)
            }
        })
        if (IsObjectType(type)) {
            const { additionalProps } = type
            if (additionalProps) {
                for (const key of (value as Y.Map<unknown>).keys()) {
                    if (!type.props.some((x) => x.name === key)) {
                        const oldValue = YTools.get(value, key)
                        const newValue = ReplaceValuesByTypeYTools(
                            oldValue,
                            additionalProps,
                            replacer,
                            visitedObjects,
                            [...path, key]
                        )
                        if (newValue !== oldValue) {
                            YTools.set(value, key, newValue)
                        }
                    }
                }
            }
        }
    } else if (IsArrayType(type)) {
        if (visitedObjects.has(value)) return value
        visitedObjects.add(value)

        for (let i = 0; i < value.length; i++) {
            const oldValue = YTools.get(value, i)
            const newValue = ReplaceValuesByTypeYTools(
                oldValue,
                type.array,
                replacer,
                visitedObjects,
                [...path, i]
            )
            if (newValue !== oldValue) {
                YTools.set(value, i, newValue)
            }
        }
    }
    return value
}
